import type { ReactNode } from 'react';
import styles from './EmptyStateTile.module.css';

export interface IEmptyStateTileProps {
  /** The glyph inside the info-tint disc — an `@utils/icons` component sized
   *  to the 40px tile (22px is the house size). */
  icon: ReactNode;
  title: string;
  body: string;
  /** The one thing to do from here — the host's own CTA button, so it keeps
   *  its panel's button styling. */
  children?: ReactNode;
  /**
   * Where the tile sits.
   * - `panel` (default): a Logistics panel body — vertical breathing room
   *   around it, body text as wide as the panel.
   * - `drawer`: the task drawer's narrow column, which centres the tile
   *   itself — no outer padding, and the body wraps early so the sentence
   *   does not run the full width of the drawer.
   */
  variant?: 'panel' | 'drawer';
}

/**
 * The guided empty state: an info-tint disc, a title, a sentence saying what
 * this surface is for, and the one action that fills it. Shared by the two
 * Logistics panels (Inventory Levels, Delivery Pipeline) and the Scheduler's
 * Stock tab, which had three byte-identical copies of the markup and the CSS
 * between them.
 */
export function EmptyStateTile({ icon, title, body, children, variant = 'panel' }: IEmptyStateTileProps) {
  return (
    <div className={styles.tile} data-variant={variant}>
      <div className={styles.icon} aria-hidden="true">
        {icon}
      </div>
      <p className={styles.title}>{title}</p>
      <p className={styles.body}>{body}</p>
      {children}
    </div>
  );
}
